import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import {
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const FTBCAForm199Instructions = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="File CA FTB Form 199 with TaxZerone | Nonprofit Filing Guide"
          description="Learn who must file CA Form 199 and how TaxZerone simplifies the process, ensuring accurate and timely e-filing for your nonprofit organization’s tax return."
          keywords=""
          canonical="resources/tax-exempt/ftb-ca-form-199-filing/"
          ogType="article"
          jsonLdPricing="9.90"
          jsonLdAggregateRatingValue="4.99"
          jsonLdAggregateRatingCount="2063"
        />
      }
    >
      <Container maxWidth="lg">
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 5,
            mt: 5,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight"> FTB CA Form 199</span>
          </span>{' '}
          Instructions
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/tax-exempt/ftb-ca-form-199-filing/"
                  />

                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-20T09:00:00-05:00"
                      >
                        July 20, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-20T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 20, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Peter Samuel
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    California Form 199 at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          What Is Form 199?{' '}
                        </strong>{' '}
                        <Link
                          href="https://www.taxzerone.com/tax-exempt/efile-ftb-ca-form199-online/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          California Form 199
                        </Link>{' '}
                        is the annual information return used by eligible{' '}
                        <Link
                          href="https://www.taxzerone.com/tax-exempt/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          tax-exempt organizations{' '}
                        </Link>
                        to report information required to maintain California
                        tax-exempt status.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Who Must File?
                        </strong>{' '}
                        Organizations with{' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          gross receipts normally greater than $50,000
                        </strong>{' '}
                        generally file Form 199.
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Private foundations{' '}
                        </strong>{' '}
                        and certain{' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          nonexempt charitable trusts under IRC Section
                          4947(a)(1){' '}
                        </strong>{' '}
                        must file regardless of gross receipts.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          What Is Reported?
                        </strong>{' '}
                        Form 199 reports{' '}
                        <strong style={{ fontWeight: 600 }}>
                          gross receipts, contributions, grants, program and
                          investment income, expenses, distributions, and other
                          information
                        </strong>{' '}
                        relevant to California tax-exempt status..
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="4" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Federal Filing Connection: Form 199 is a California
                          filing requirement separate from the federal Form 990
                          series;
                        </strong>
                        an organization may need to file both its applicable
                        federal return and California Form 199.
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>

                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Stack
                  direction={{ xs: 'column-reverse', md: 'row' }}
                  spacing={{ xs: 1, sm: 2, md: 3 }}
                  sx={{ mb: 0 }}
                >
                  <Box>
                    <Typography
                      variant="h2"
                      sx={{
                        fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                        fontWeight: 600,
                        mb: 2,
                      }}
                    >
                      Introduction
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      <Link
                        href="/tax-exempt/efile-ftb-ca-form199-online/"
                        sx={{ color: '#0202ff' }}
                      >
                        {' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          CA Form 199
                        </strong>
                      </Link>
                      , also known as the{' '}
                      <i>
                        California Exempt Organization Annual Information Return
                      </i>
                      , is a tax form that must be filed by certain nonprofit
                      organizations that are exempt from federal income tax
                      under Section 501(c) of the Internal Revenue Code.
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      This form is used to report information about the
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        organization&apos;s activities, finances, and governance
                      </span>
                      . Filing this form ensures that the organization is in
                      compliance with California tax regulations and remains in
                      good standing with the state.
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      In this resource article, we help understand{' '}
                      <span style={{ fontWeight: 600 }}> CA Form 199</span> by
                      clarifying who must file, the filing requirements, and
                      addressing commonly asked questions.
                    </Typography>

                    <Box
                      sx={{
                        pt: 2,
                      }}
                    >
                      <Typography variant="body1" sx={{ mb: 0.5 }}>
                        <strong style={{ fontWeight: 600 }}>
                          Table of Contents
                        </strong>
                      </Typography>
                      <List
                        sx={{
                          mb: 5,
                          border: '1px solid #ccc',
                          borderRadius: 2,
                        }}
                      >
                        <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                          <ListItemIcon
                            sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                          >
                            <DoubleArrowIcon />
                          </ListItemIcon>
                          <ListItemText>
                            <Link href="#what" sx={{ color: '#0202ff' }}>
                              What is CA Form 199?
                            </Link>
                          </ListItemText>
                        </ListItem>
                        <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                          <ListItemIcon
                            sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                          >
                            <DoubleArrowIcon />
                          </ListItemIcon>
                          <ListItemText>
                            <Link href="#who" sx={{ color: '#0202ff' }}>
                              Who must file CA Form 199?
                            </Link>
                          </ListItemText>
                        </ListItem>
                        <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                          <ListItemIcon
                            sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                          >
                            <DoubleArrowIcon />
                          </ListItemIcon>
                          <ListItemText>
                            <Link href="#instr" sx={{ color: '#0202ff' }}>
                              Instructions for CA Form 199
                            </Link>
                          </ListItemText>
                        </ListItem>

                        <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                          <ListItemIcon
                            sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                          >
                            <DoubleArrowIcon />
                          </ListItemIcon>
                          <ListItemText>
                            <Link href="#choose" sx={{ color: '#0202ff' }}>
                              Choose TaxZerone to complete your CA Form 199
                              filing
                            </Link>
                          </ListItemText>
                        </ListItem>
                        <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                          <ListItemIcon
                            sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                          >
                            <DoubleArrowIcon />
                          </ListItemIcon>
                          <ListItemText>
                            <Link href="#common" sx={{ color: '#0202ff' }}>
                              Commonly Asked Questions
                            </Link>
                          </ListItemText>
                        </ListItem>
                      </List>
                    </Box>
                    <Typography
                      variant="h2"
                      sx={{
                        fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                        fontWeight: 600,
                        mb: 0.5,
                        scrollMarginTop: 88,
                      }}
                      id="what"
                    >
                      What is CA Form 199?
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      <span style={{ fontWeight: 600 }}> CA Form 199 </span> is
                      a mandatory filing for most{' '}
                      <span style={{ fontWeight: 600 }}>
                        tax-exempt organizations
                      </span>{' '}
                      operating in California. The form is used to report
                      information relevant to maintaining exempt organizations’
                      tax-exempt status.
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      It provides the state with vital information about the
                      financial activities and operations of these
                      organizations, ensuring they continue to meet the
                      requirements for their tax-exempt status.
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 4 }}>
                      It also allows the FTB to{' '}
                      <span style={{ fontWeight: 600 }}>
                        monitor the income, donations, and other key financial
                        transactions
                      </span>{' '}
                      of exempt organizations.
                    </Typography>
                  </Box>
                </Stack>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="who"
                >
                  Who must file CA Form 199?
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Exempt organizations must answer the following questions to
                  determine whether they should file Form 199.
                </Typography>
                <ol style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 16 }}>
                    Did the organization receive a letter from the FTB granting
                    tax-exempt status to the organization?
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Is the organization a nonexempt charitable trust as
                    described in IRC Section 4947(a)(1)?
                  </li>
                </ol>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If the answer to both of these questions is “No,” your
                  organization is not required to file CA Form 199.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If the answer to questions 1 or 2 is “Yes,” the organization
                  may be required to file Form 199 depending upon the type of
                  exempt organization.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 2, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <strong style={{ fontWeight: 600 }}> Note :</strong> However,
                  some organizations may be exempt from filing CA Form 199 based
                  on their annual gross receipts. Typically, if an
                  organization’s annual gross receipts are less than $50,000, it
                  may be eligible to file the{' '}
                  <strong style={{ fontWeight: 600 }}>
                    {' '}
                    Form 990-N (e-postcard)
                  </strong>{' '}
                  instead.
                </Alert>
                <Box
                  component="div"
                  sx={{ mb: 5, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: '18px !important',
                          fontWeight: 600,
                          mb: 1,
                          lineHeight: 1.4,
                        }}
                      >
                        Choose TaxZerone - Your Gateway to Seamless Tax Filing
                      </Typography>
                      <Typography variant="body2">
                        Complete your CA Form 199 e-filing requirements with
                        ease!
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 200, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=FTBCA199instructions`}
                      >
                        E-file CA Form 199
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="instr"
                >
                  Instructions for CA Form 199
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Below, we have provided CA Form 199 filing requirements for
                  each part.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1.5,
                    lineHeight: 1.4,
                  }}
                >
                  CA Form 199 Part I
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 1 – Gross sales or receipts from other sources
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter in this line the amount from Side 2, Part II, line 8.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  However, don’t include amounts for gross dues and assessments
                  from members and affiliates or amounts from gross
                  contributions, gifts, grants, and similar amounts received.
                  These amounts are reported in Part I, line 2 and line 3.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <strong style={{ fontWeight: 600 }}> Line 2</strong> – Enter
                  here the gross dues and assessments from members and
                  affiliates
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 3 -Gross contributions, gifts, grants, and similar
                  amounts received
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Attach an itemized schedule if money, securities, or other
                  property aggregating $5,000 or more is received directly or
                  indirectly from one person in one or more transactions during
                  the year. The schedule must show the name, address, date
                  received, and the total amount received from each person.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 4 - Total gross receipts for filing requirement test.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add line 1 through line 3 and enter the sum value here. If the
                  result is less than $50,000, see General Information B.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <strong style={{ fontWeight: 600 }}> Line 5</strong> – Enter
                  in this line the cost of goods sold
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <strong style={{ fontWeight: 600 }}> Line 6</strong> – Enter
                  here the cost or other basis, and sales expenses of assets
                  sold
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 7 -Total costs.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add line 5 and line 6 and enter the sum value in this line.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 8 - Total Gross Income
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Subtract line 7 from line 4 and enter the difference here.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 9 - Total expenses and disbursements.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the value from From Side 2, Part II, line 18
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong style={{ fontWeight: 600 }}> Line 10</strong> - Excess
                  of receipts over expenses and disbursements
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Subtract line 9 from line 8 and enter the difference here.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong style={{ fontWeight: 600 }}> Line 11</strong> - Enter
                  the Total payments in this line
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 12 - Use Tax
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The following businesses are required to report purchases
                  subject to use tax directly to the California Department of
                  Tax and Fee Administration and may not report use tax on their
                  income tax return:
                </Typography>
                <ul style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 12 }}>
                    Businesses that have, or are required to have, a California
                    seller’s permit.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Businesses that are not required to hold a California
                    seller’s permit, but receive at least $100,000 in gross
                    receipts.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Businesses that are otherwise required to be registered with
                    the California Department of Tax and Fee Administration for
                    sales or use tax purposes.
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  An exempt organization that is not required to report
                  purchases subject to use tax directly to the California
                  Department of Tax and Fee Administration may, with some
                  exceptions, report use tax on its California Exempt
                  Organization Annual Information Return.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 13 - Payments balance
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If line 11 is more than line 12, subtract line 12 from line 11
                  and enter the value in this line.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 14 - Use tax balance
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If line 12 is more than line 11, subtract line 11 from line 12
                  and enter the value in this line.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 15 - Penalties and interest
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <strong style={{ fontWeight: 600 }}>
                    {' '}
                    Failure to File a Timely Return
                  </strong>{' '}
                  – An organization that fails to file the return on or before
                  the original due date, or extended due date, is assessed a
                  penalty of $5 for each month, or part of the month, if the
                  return is late. If the return is not filed by the extended due
                  date, the automatic extension will not apply. The penalty may
                  not exceed $40.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <strong style={{ fontWeight: 600 }}>
                    {' '}
                    Failure to Furnish Information
                  </strong>{' '}
                  – In the case of a private foundation, the FTB may make a
                  written demand that a delinquent return or foundation report
                  be filed within a reasonable amount of time after mailing a
                  demand notice. The person who fails to file after such demand
                  is subject to a penalty of $5 for each month, or part of the
                  month, (not to exceed $25) after the period expires.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <strong style={{ fontWeight: 600 }}> Interest</strong> –
                  Interest accrues on the delinquent penalty from the original
                  due date of the return until the penalty is paid.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 16 - Balance due
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Add line 12 and line 15. Then subtract line 11 from the result
                  and enter that difference value here
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  CA Form 199 Part II
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong style={{ fontWeight: 600 }}> Line 1</strong> - Enter
                  in this line the gross sales or receipts from all business
                  activities
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Do not include amounts for gross dues and assessments from
                  members and affiliates or amounts from gross contributions,
                  gifts, grants, and similar amounts received. Report these
                  amounts on Side 1, Part I, line 2, and line 3, respectively.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong style={{ fontWeight: 600 }}> Line 2</strong> - Enter
                  the total Interest here
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong style={{ fontWeight: 600 }}> Line 3</strong> - Enter
                  in this line the Dividends
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong style={{ fontWeight: 600 }}> Line 4</strong> - Enter
                  in line 4 the total Gross rents
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong style={{ fontWeight: 600 }}> Line 5</strong> - Enter
                  here the total amount of Gross royalties
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong style={{ fontWeight: 600 }}> Line 6</strong> - Gross
                  amount received from the sale of assets
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the gross sales price on Side 2, Part II, line 6 and the
                  total and enter the cost or other basis, expenses, etc. (less
                  depreciation if applicable), on Side 1, Part I, line 6.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong style={{ fontWeight: 600 }}> Line 7</strong> - Other
                  income
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Attach a schedule showing other income not listed in line 1
                  through line 6.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong style={{ fontWeight: 600 }}> Line 8</strong> - Total
                  gross sales or receipts from other sources
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add line 1 through line 7, and enter the sum value on line 8
                  and Side 1, Part I, line 1.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 9 – Contributions, gifts, grants, and similar amounts
                  paid
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Private foundations, regardless of gross receipts, and other
                  organizations required to file Form 199, must attach a
                  schedule to support contributions, gifts, grants,
                  scholarships, etc., showing all of the following:
                </Typography>
                <ul style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 12 }}>Each class of activity.</li>
                  <li style={{ marginBottom: 12 }}>
                    Separate totals for each activity.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Name and address of the donee and the amount of the
                    distribution to the donee.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Relationship of the donee, if related by blood, marriage,
                    registered domestic partnership, adoption, or employment
                    (including children of employees) to any person or
                    corporation having an interest in the organization (such as
                    creator, donor, director, trustee, officer, etc.).
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <strong style={{ fontWeight: 600 }}> Line 10</strong> - Enter
                  the amount of disbursements provided to or collected for
                  members.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <strong style={{ fontWeight: 600 }}> Line 11</strong> -
                  Compensation of officers, directors, and trustees
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total compensation paid to current officers,
                  directors, trustees, and key employees for the organization’s
                  taxable year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Compensation includes all forms of income and other benefits
                  earned or received in return for services rendered, including
                  pension plan contributions, and other employee benefits, but
                  does not include non-compensatory expense reimbursements or
                  allowances.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report all compensation amounts relating to such an
                  individual, including those related to services performed in a
                  capacity other than as an officer, director, trustee, or key
                  employee.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 12 – Other salaries and wages
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter in this line the total amount of employee salaries,
                  wages, fees, bonuses, severance payments, and similar amounts
                  not reported on line 10 or line 11.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <strong style={{ fontWeight: 600 }}> Line 13</strong> - Enter
                  the value of interest in this line
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <strong style={{ fontWeight: 600 }}> Line 14</strong> - Enter
                  here the total taxes
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <strong style={{ fontWeight: 600 }}> Line 15</strong> - Enter
                  in line 15 the total rents.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
                  Line 16 - Depreciation and depletion
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  For Corporations and associations
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Complete form FTB 3885, Corporation Depreciation and
                  Amortization. Enter the total from form FTB 3885, line 16, and
                  line 20 on Form 199, Side 2, Part II, line 16. Attach form FTB
                  3885 to Form 199.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Trusts
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Complete form FTB 3885F, Depreciation and Amortization. Enter
                  the total from form FTB 3885F, line 6, on Form 199, Side 2,
                  Part II, line 16, and attach form FTB 3885F to Form 199.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 17 – Other expenses and disbursements
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Attach a schedule showing expenses and disbursements not
                  listed in line 9 through line 16.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 18 - Total expenses and disbursements.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Add line 9 through line 17, and enter the sum value here and
                  on Side 1, Part I, line 9
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="choose"
                >
                  Choose TaxZerone to complete your CA Form 199 filing
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  TaxZerone offers a hassle-free solution to help tax-exempt
                  organizations meet their annual filing requirements.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  What We Offer
                </Typography>
                <ul style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 12 }}>
                    <strong style={{ fontWeight: 600 }}>
                      {' '}
                      Efficient Process
                    </strong>{' '}
                    - Our guided platform simplifies the filing process,
                    ensuring that you only answer the questions relevant to your
                    organization.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    <strong style={{ fontWeight: 600 }}>
                      {' '}
                      Accurate Filing
                    </strong>{' '}
                    - TaxZerone helps you avoid costly mistakes by ensuring all
                    data is entered correctly and submitted on time.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    <strong style={{ fontWeight: 600 }}>
                      {' '}
                      Dedicated Support
                    </strong>{' '}
                    - Our tax experts can guide you through the filing process,
                    ensuring that your form is completed accurately and on time.
                  </li>
                </ul>

                <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
                  Here’s how your CA Form 199 is transmitted to the FTB in 3
                  simple steps!
                </Typography>
                <ul style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 12 }}>
                    <strong style={{ fontWeight: 600 }}>
                      {' '}
                      Provide Organization Details
                    </strong>{' '}
                    - Choose the tax year for which you want to file{' '}
                    <Link
                      href="/tax-exempt/efile-ftb-ca-form199-online/ "
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      CA Form 199
                    </Link>
                    , and provide your organization’s details.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    <strong style={{ fontWeight: 600 }}>
                      {' '}
                      Preview CA Form 199
                    </strong>{' '}
                    - Preview the information provided in your{' '}
                    <span style={{ fontWeight: 600 }}>CA 199 return</span> for
                    accuracy before transmitting.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    <strong style={{ fontWeight: 600 }}>
                      {' '}
                      Transmit to the FTB
                    </strong>{' '}
                    - Transmit your{' '}
                    <span style={{ fontWeight: 600 }}> CA 199 form</span> to the
                    <span style={{ fontWeight: 600 }}> FTB </span> and get the
                    acceptance in just a few hours.
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  With our platform, you can avoid the complexities of Form 199
                  filing and focus on your organization&apos;s mission.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  In case of rejection for your CA Form 199 return, worry not!
                  We offer a hassle-free correction and retransmission process,
                  absolutely free of charge!
                </Typography>

                <Box
                  component="div"
                  sx={{
                    mb: 4,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    backgroundSize: {
                      xs: '20% auto',
                      sm: '20% auto',
                      md: '20% auto',
                    },
                    border: '1px solid #ccc',
                    display: 'flex',
                    width: '100%',
                    alignItems: 'center',
                    flexDirection: { xs: 'column', md: 'row' },
                  }}
                  // className="wave-bg"
                >
                  <Box
                    sx={{ width: '100%', flexGrow: 1, mb: { xs: 1, md: 0 } }}
                  >
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 600,
                        mb: 0,
                        fontSize: '20px !important',
                      }}
                    >
                      Embrace the Future of Tax Filing – Choose TaxZerone Today!
                    </Typography>
                    <Typography variant="body2">
                      Make your e-filing process simple by clicking the button
                      below.
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right', minWidth: 200 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      sx={{
                        textTransform: 'none',
                        boxShadow: 'none',
                        borderRadius: 2,
                        mb: 0,
                      }}
                      size="large"
                      href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=FTBCA199instructions`}
                    >
                      Get Started
                    </Button>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Container maxWidth="lg" id="common" sx={{ my: 0 }}>
        <Grid
          container
          spacing={2}
          mx={2}
          direction="row"
          justifyContent="center"
          className="MuiGrid-root-width"
          sx={{ my: { xs: 0, sm: 0 } }}
        >
          <Grid
            size={{ xs: 12, sm: 10, md: 9 }}
            textAlign="left"
            my={{ xs: 2, sm: 5 }}
            mb={3}
            className="responsive"
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 5,
                textAlign: 'center',
              }}
            >
              Commonly Asked Questions
            </Typography>
            <Box
              component="div"
              sx={{ mb: 5 }}
              itemScope
              itemType="https://schema.org/FAQPage"
            >
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  1. What is the deadline for filing CA Form 199?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component="div" itemProp="text">
                    <Typography variant="body1" sx={{ mt: 1, mb: 1 }}>
                      The deadline for filing CA Form 199 (California Exempt
                      Organization Annual Information Return) is the{' '}
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        15th day of the 5th month
                      </span>{' '}
                      after the end of the organization’s accounting period.
                    </Typography>
                    <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                      <li>
                        For organizations that follow the calendar year (ending
                        December 31), the due date is{' '}
                        <span style={{ fontWeight: 600 }}>May 15</span> of the
                        following year.{' '}
                      </li>
                      <li>
                        If needed, you may request an automatic extension of up
                        to <span style={{ fontWeight: 600 }}>6 months</span> to
                        file, but this does{' '}
                        <span style={{ fontWeight: 600 }}>
                          not extend the time to pay any taxes due
                        </span>
                        .{' '}
                      </li>
                    </Box>
                  </Box>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  2. Can small organizations skip filing CA Form 199?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component="div" itemProp="text">
                    <Typography variant="body1" sx={{ mb: 0, mt: 1 }}>
                      If an organization’s annual gross receipts are{' '}
                      <span style={{ fontWeight: 600 }}>less than $50,000</span>
                      , it is eligible to file{' '}
                      <span style={{ fontWeight: 600 }}>
                        CA Form 199N <br />
                        (e-postcard)
                      </span>
                      instead. Organizations exceeding this threshold are
                      required to file{' '}
                      <Link
                        href="/tax-exempt/efile-ftb-ca-form199-online/"
                        sx={{ color: '#0202ff' }}
                      >
                        CA Form 199
                      </Link>
                      .
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  3. Can I get an extension to file CA Form 199?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component="div" itemProp="text">
                    <Typography variant="body1" sx={{ mt: 1, mb: 0 }}>
                      Yes. California automatically grants a{' '}
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        6-month extension
                      </span>
                      to file{' '}
                      <span style={{ fontWeight: 600 }}>CA Form 199</span>{' '}
                      (California Exempt Organization Annual Information
                      Return).
                    </Typography>
                    <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                      <li>
                        <span style={{ fontWeight: 600 }}>
                          No separate form is required
                        </span>{' '}
                        to request this extension.{' '}
                      </li>
                      <li>
                        Your organization must be{' '}
                        <span style={{ fontWeight: 600 }}>
                          in good standing
                        </span>{' '}
                        and{' '}
                        <span style={{ fontWeight: 600 }}>not suspended</span>{' '}
                        as of the original due date.{' '}
                      </li>
                      <li>
                        This extension is for{' '}
                        <span style={{ fontWeight: 600 }}>filing only</span>,
                        not for payment.{' '}
                      </li>
                    </Box>
                    <Typography variant="body1">
                      If your organization owes taxes, you must{' '}
                      <span style={{ fontWeight: 600 }}>
                        pay by the original due date
                      </span>{' '}
                      using{' '}
                      <span style={{ fontWeight: 600 }}>Form FTB 3539</span>{' '}
                      (Payment for Automatic Extension for Corporations and
                      Exempt Organizations) to avoid penalties and
                      interest.{' '}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </AppLayout>
  );
};

export default FTBCAForm199Instructions;
